/**
 * Aware Partner Network — Phase 5A admin dashboard widget.
 *
 * Surfaces region-allocated bookings that need admin attention.
 *   • Top section  → review_required (multi-candidate, partner_id null)
 *   • Bottom section → recent auto-allocated (last 7 days, informational)
 *
 * Admin-only — the underlying endpoint is gated by require_admin.
 * Lab-tech / customer / partner / unauthenticated roles cannot read it.
 * READ-ONLY widget: never mutates partners, bookings, commission, or
 * white-label flags; never sends emails.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, MapPin, ArrowRight, CheckCircle2, Inbox, Loader2,
} from "lucide-react";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

function parseSuburbPostcode(row) {
  // Prefer the resolver's parsed values; fall back to the customer's
  // free-text address tail so the widget is informative even on the
  // edge case where parsing missed a token.
  const suburb = row.allocation_matched_suburb || "";
  const postcode = row.allocation_matched_postcode || "";
  if (suburb || postcode) return `${suburb}${suburb && postcode ? " " : ""}${postcode}`.trim();
  return row.customer?.address || "—";
}

function CandidatePartnerList({ candidates, fallbackIds }) {
  // We list at most 4 partners to keep the row compact; the 5th+ rolls
  // up into a "+N more" pill so the widget never grows tall.
  const shown = candidates.slice(0, 4);
  const overflow = Math.max(0, candidates.length - shown.length);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="region-queue-candidates">
      {shown.map((p) => (
        <span
          key={p.id}
          data-testid={`region-queue-candidate-${p.id}`}
          className="inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 bg-white border border-amber-300 text-amber-900"
          title={`${p.business_name}${p.franchise_region ? ` — ${p.franchise_region}` : ""}`}
        >
          {p.business_name || p.id}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">
          +{overflow} more
        </span>
      )}
      {candidates.length === 0 && fallbackIds.length > 0 && (
        <span className="text-[11px] text-slate-500 italic">
          {fallbackIds.length} candidate id{fallbackIds.length === 1 ? "" : "s"} (partner rows missing)
        </span>
      )}
    </div>
  );
}

export function RegionAllocationReviewWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/pws-partners/region-allocation-queue?days=7")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Unable to load region allocation queue"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section
        data-testid="region-allocation-widget"
        className="mt-6 rounded-3xl bg-white border border-slate-200 p-5"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading region allocation review queue…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="region-allocation-widget"
        className="mt-6 rounded-3xl bg-rose-50 border border-rose-200 p-5 text-rose-800 text-sm"
      >
        {error}
      </section>
    );
  }

  const review = data?.review_required || [];
  const recent = data?.recent_region_matches || [];

  return (
    <section
      data-testid="region-allocation-widget"
      className="mt-6 rounded-3xl bg-white border border-slate-200 overflow-hidden"
    >
      {/* ── Review-required header + body ──────────────────────────── */}
      <div className="flex items-center justify-between gap-4 px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800">
              Region Allocation Review
            </div>
            <div className="text-sm font-semibold text-slate-900" data-testid="region-allocation-widget-title">
              {review.length === 0
                ? "No region allocation reviews pending"
                : `${review.length} booking${review.length === 1 ? "" : "s"} need${review.length === 1 ? "s" : ""} admin review`}
            </div>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-800">
          last {data?.lookback_days ?? 7}d
        </span>
      </div>

      {review.length === 0 ? (
        <div
          className="px-5 py-8 text-center"
          data-testid="region-allocation-widget-empty"
        >
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
          <div className="text-sm font-semibold text-slate-700">All clear</div>
          <div className="text-xs text-slate-500 mt-1">
            No bookings are waiting for region-allocation review.
            Ambiguous matches will appear here so the team can assign a
            partner manually — Phase 4 never assigns randomly.
          </div>
        </div>
      ) : (
        <ul
          className="divide-y divide-amber-100"
          data-testid="region-allocation-widget-list"
        >
          {review.map((b) => (
            <li
              key={b.reference}
              data-testid={`region-queue-row-${b.reference}`}
              className="px-5 py-3.5 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between hover:bg-amber-50/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <code
                    data-testid={`region-queue-row-${b.reference}-ref`}
                    className="text-[11px] font-mono font-bold text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded"
                  >
                    {b.reference}
                  </code>
                  <span
                    data-testid={`region-queue-row-${b.reference}-customer`}
                    className="text-sm font-semibold text-slate-900 truncate"
                  >
                    {b.customer?.name || "—"}
                  </span>
                  <span
                    data-testid={`region-queue-row-${b.reference}-location`}
                    className="text-xs text-slate-600 inline-flex items-center gap-1"
                  >
                    <MapPin className="w-3 h-3 text-slate-400" /> {parseSuburbPostcode(b)}
                  </span>
                  <span
                    data-testid={`region-queue-row-${b.reference}-date`}
                    className="text-xs text-slate-500"
                  >
                    {fmtDate(b.created_at)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    data-testid={`region-queue-row-${b.reference}-method`}
                    className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-200 text-amber-900"
                  >
                    review required
                  </span>
                  <span
                    data-testid={`region-queue-row-${b.reference}-candidate-count`}
                    className="text-[11px] text-slate-600"
                  >
                    {b.candidate_partner_count || 0} eligible candidate{(b.candidate_partner_count || 0) === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="mt-2">
                  <CandidatePartnerList
                    candidates={b.candidate_partners || []}
                    fallbackIds={b.allocation_candidate_partner_ids || []}
                  />
                </div>
              </div>
              <Link
                to={`/admin/bookings/${b.reference}`}
                data-testid={`region-queue-row-${b.reference}-review-link`}
                className="self-start lg:self-center inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-cyan-700 rounded-full px-3.5 py-1.5 transition-colors whitespace-nowrap"
              >
                Review booking <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* ── Recent (informational) section ─────────────────────────── */}
      <div
        className="px-5 py-3 bg-slate-50 border-t border-slate-200 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 flex items-center gap-2"
        data-testid="region-allocation-widget-recent-header"
      >
        <Inbox className="w-3.5 h-3.5" />
        Recent auto-allocated bookings · last {data?.lookback_days ?? 7}d ·
        <span className="text-slate-500 normal-case tracking-normal font-normal">
          {recent.length} booking{recent.length === 1 ? "" : "s"} assigned to a single eligible partner
        </span>
      </div>
      {recent.length === 0 ? (
        <div className="px-5 py-4 text-xs text-slate-500" data-testid="region-allocation-widget-recent-empty">
          No region-allocated bookings in the last {data?.lookback_days ?? 7} day{(data?.lookback_days ?? 7) === 1 ? "" : "s"}.
        </div>
      ) : (
        <ul
          className="divide-y divide-slate-100"
          data-testid="region-allocation-widget-recent-list"
        >
          {recent.map((b) => (
            <li
              key={b.reference}
              data-testid={`region-recent-row-${b.reference}`}
              className="px-5 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                <code className="text-[11px] font-mono font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">
                  {b.reference}
                </code>
                <span className="text-sm font-semibold text-slate-900 truncate">
                  {b.customer?.name || "—"}
                </span>
                <span className="text-xs text-slate-600 inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-400" /> {parseSuburbPostcode(b)}
                </span>
                <span className="text-xs text-slate-500">{fmtDate(b.created_at)}</span>
                {b.partner ? (
                  <span
                    data-testid={`region-recent-row-${b.reference}-partner`}
                    className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200"
                    title={b.partner.franchise_region ? `${b.partner.business_name} — ${b.partner.franchise_region}` : b.partner.business_name}
                  >
                    {b.partner.business_name || b.partner.partner_slug}
                  </span>
                ) : null}
              </div>
              <Link
                to={`/admin/bookings/${b.reference}`}
                data-testid={`region-recent-row-${b.reference}-view`}
                className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 hover:underline inline-flex items-center gap-1 whitespace-nowrap"
              >
                View <ArrowRight className="w-3 h-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
